/**
 * PATH: packages/email-hub/src/feature/reader-header/runtime/focusManager.ts
 * FILE_ID: EMAILHUB-READERHEADER-RUNTIME-FOCUS-001
 * PURPOSE: Deterministic keyboard-focus restoration for reader actions:
 *   terminal advance → new email's command region; menu/picker close →
 *   trigger; undo / terminal failure → prior focus.
 * ROLE IN ARCHITECTURE: DOM utility leaf under the action runner and menus.
 * MAIN EXPORTS: captureFocus, focusReaderCommandRegion.
 * NON-RESPONSIBILITIES: No focus trapping (dialogs own their traps).
 * NOTES FOR FUTURE AI: Selectors target data attributes the command bar
 *   already renders (data-testid="reader-command-bar", data-reader-command).
 *   Keep them in sync with CommandBar.tsx.
 */

export interface FocusRestore {
  /** Re-focus the captured element if it is still in the document. */
  restore: () => void
}

/**
 * Capture the currently focused element for later restoration.
 *
 * Output: a FocusRestore whose `restore()` is a safe no-op when the element
 * was removed or never existed. Side effects: none at capture time.
 */
export function captureFocus(doc: Document = document): FocusRestore {
  const element =
    doc.activeElement instanceof HTMLElement ? doc.activeElement : null
  return {
    restore: () => {
      if (element && element.isConnected) {
        element.focus()
      }
    },
  }
}

/**
 * Focus the reader command region (used after a terminal advance so the
 * keyboard lands on the NEW email's commands, plan focus contract).
 *
 * Strategy: primary Reply command → first enabled command-bar button → the
 * bar itself. Returns true when something received focus.
 */
export function focusReaderCommandRegion(root: ParentNode = document): boolean {
  const bar = root.querySelector<HTMLElement>(
    '[data-testid="reader-command-bar"]',
  )
  if (!bar) return false
  const target =
    bar.querySelector<HTMLElement>('[data-reader-command="reply"]:not(:disabled)') ??
    bar.querySelector<HTMLElement>('button:not(:disabled)') ??
    bar
  if (target === bar && !bar.hasAttribute('tabindex')) {
    bar.setAttribute('tabindex', '-1')
  }
  target.focus()
  return root instanceof Document
    ? root.activeElement === target
    : target.ownerDocument.activeElement === target
}
